<template>
  <div class="camp-overview-page">
    <b-container>
      <div class="page-header">
        <h1>課程總覽</h1>
        <b-button variant="outline-primary" @click="_go('./camps')">返回系列</b-button>
      </div>

      <div class="overview-toolbar mb-4">
        <b-form-select
          v-model="selectedSeriesId"
          :options="seriesOptions"
          class="toolbar-select"
          aria-label="營隊系列篩選"
          @change="getList"
        ></b-form-select>
        <b-form-select
          v-model="selectedSeasonType"
          :options="seasonOptions"
          class="toolbar-select"
          aria-label="季別篩選"
          @change="getList"
        ></b-form-select>
        <b-form-select
          v-model="selectedStatus"
          :options="statusOptions"
          class="toolbar-select"
          aria-label="報名狀態篩選"
        ></b-form-select>
        <b-form-select
          v-model="sortMode"
          :options="sortModeOptions"
          class="toolbar-select"
          aria-label="排序方式"
        ></b-form-select>
        <div class="overview-search-group">
          <b-input-group class="overview-search">
            <b-form-input v-model="keywords" placeholder="搜尋課程或梯次" aria-label="搜尋課程或梯次" @keyup.enter="getList" />
            <b-input-group-append>
              <b-button variant="primary" @click="getList"><b-icon-search class="mr-1" />搜尋</b-button>
            </b-input-group-append>
          </b-input-group>
          <b-button
            v-if="hasActiveFilters"
            variant="outline-secondary"
            class="clear-filters-button"
            @click="clearFilters"
          >
            <b-icon-x-circle class="mr-1" />清除條件
          </b-button>
        </div>
      </div>

      <div v-if="loading" class="text-center py-5"><b-spinner variant="primary" /></div>
      <b-alert v-else-if="programGroups.length === 0" show variant="warning" class="text-center">
        目前沒有符合條件的課程梯次
      </b-alert>

      <section v-for="program in programGroups" v-else :key="program.id" class="program-card">
        <button type="button" class="program-header" :aria-expanded="String(isExpanded(program.id))" @click="toggleProgram(program.id)">
          <span class="program-heading">
            <span class="program-icon"><b-icon-book-half /></span>
            <span><strong>{{ program.name }}</strong><small>適合年級：{{ gradeText(program) }}</small></span>
          </span>
          <span class="program-actions">
            <span class="product-count">{{ program.products.length }} 個梯次</span>
            <b-icon :icon="isExpanded(program.id) ? 'chevron-up' : 'chevron-down'" />
          </span>
        </button>

        <div v-show="isExpanded(program.id)">
          <div v-if="program.commonBenefits.length" class="common-benefits">
            <span class="common-benefits-label"><b-icon-tags-fill class="mr-1" />共同優惠</span>
            <div class="benefit-scroll">
              <span v-for="benefit in program.commonBenefits" :key="benefit.key" class="benefit-chip">
                <span class="benefit-chip-title">{{ benefit.title }}</span>
              </span>
            </div>
          </div>
          <div class="desktop-products d-none d-md-block">
            <table>
              <thead><tr><th>梯次</th><th>日期</th><th>報名期間</th><th>費用</th><th>優惠</th><th>狀態</th><th>操作</th></tr></thead>
              <tbody>
                <tr v-for="product in program.products" :key="product.ProductId">
                  <td>{{ productName(product) }}</td>
                  <td>{{ dateRange(product.ProductStart, product.ProductEnd) }}</td>
                  <td>{{ dateRange(product.ApplyStart, product.ApplyEnd) }}</td>
                  <td>
                    <div class="price-display">
                      <template v-if="earlyBirdActive(product)">
                        <small>早鳥價</small><strong>NT${{ money(product.EarlyBirdPrice) }}</strong>
                        <del v-if="product.Price != null">售價 NT${{ money(product.Price) }}</del>
                      </template>
                      <template v-else-if="product.Price != null && Number(product.OriginalPrice) > Number(product.Price)">
                        <del>原價 NT${{ money(product.OriginalPrice) }}</del><strong>NT${{ money(product.Price) }}</strong>
                      </template>
                      <strong v-else-if="product.Price != null">NT${{ money(product.Price) }}</strong>
                      <strong v-else-if="product.DisplayPrice != null">NT${{ money(product.DisplayPrice) }}</strong>
                      <span v-else>—</span>
                    </div>
                  </td>
                  <td>
                    <div class="benefit-list">
                      <span v-if="product.uniqueBenefits.length === 0" class="text-muted">—</span>
                      <span v-for="benefit in product.uniqueBenefits" :key="benefit.key" class="benefit-chip">
                        <span class="benefit-chip-title">{{ benefit.title }}</span>
                      </span>
                    </div>
                  </td>
                  <td><b-badge :variant="applyStatus(product).variant">{{ applyStatus(product).text }}</b-badge></td>
                  <td><b-button variant="outline-primary" @click="goProgram(program.id)">查看課程</b-button></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="mobile-products d-md-none">
            <article v-for="product in program.products" :key="product.ProductId" class="mobile-product">
              <div class="mobile-product-main">
                <div><small>{{ productName(product) }}</small><strong>{{ dateRange(product.ProductStart, product.ProductEnd) }}</strong></div>
                <b-badge :variant="applyStatus(product).variant">{{ applyStatus(product).text }}</b-badge>
              </div>
              <dl>
                <div><dt><b-icon-calendar-range class="dt-icon" /><span class="dt-text">報名期間</span></dt><dd>{{ dateRange(product.ApplyStart, product.ApplyEnd) }}</dd></div>
                <div>
                  <dt><b-icon-cash class="dt-icon" /><span class="dt-text">費用</span></dt>
                  <dd>
                    <div class="price-display">
                      <template v-if="earlyBirdActive(product)">
                        <small>早鳥價</small><strong>NT${{ money(product.EarlyBirdPrice) }}</strong>
                        <del v-if="product.Price != null">售價 NT${{ money(product.Price) }}</del>
                      </template>
                      <template v-else-if="product.Price != null && Number(product.OriginalPrice) > Number(product.Price)">
                        <del>原價 NT${{ money(product.OriginalPrice) }}</del><strong>NT${{ money(product.Price) }}</strong>
                      </template>
                      <strong v-else-if="product.Price != null">NT${{ money(product.Price) }}</strong>
                      <strong v-else-if="product.DisplayPrice != null">NT${{ money(product.DisplayPrice) }}</strong>
                      <span v-else>—</span>
                    </div>
                  </dd>
                </div>
                <div>
                  <dt><b-icon-tag-fill class="dt-icon" /><span class="dt-text">優惠</span></dt>
                  <dd>
                    <div class="benefit-list">
                      <span v-if="product.uniqueBenefits.length === 0" class="text-muted">—</span>
                      <span v-for="benefit in product.uniqueBenefits" :key="benefit.key" class="benefit-chip">
                        <span class="benefit-chip-title">{{ benefit.title }}</span>
                      </span>
                    </div>
                  </dd>
                </div>
              </dl>
              <b-button block variant="outline-primary" @click="goProgram(program.id)">查看課程 <b-icon-arrow-right-short /></b-button>
            </article>
          </div>
        </div>
      </section>
    </b-container>
  </div>
</template>

<script>
import { apiGetCampOverviewList, apiGetCampSeasonTypeSelector, apiGetCampSeries } from '@/api/API.js';
import { groupCampOverview } from '@/utils/campOverviewBenefits.js';

const SORT_FIELD_MAP = {
  productStartAsc: ['ProductStart', 1],
  productStartDesc: ['ProductStart', -1],
  applyStartAsc: ['ApplyStart', 1],
  applyStartDesc: ['ApplyStart', -1],
};

export default {
  name: 'CampOverview',
  data() {
    return {
      loading: false,
      list: [],
      seriesList: [],
      selectedSeriesId: '',
      selectedSeasonType: '',
      selectedStatus: '',
      sortMode: '',
      keywords: '',
      expandedPrograms: {},
      seasonTypeDictionaryOptions: [],
    };
  },
  computed: {
    seriesOptions() {
      return [{ value: '', text: '全部系列' }, ...this.seriesList.map((series) => ({ value: series.Id, text: series.Name }))];
    },
    seasonOptions() {
      return [{ value: '', text: '全部類型' }, ...this.seasonTypeDictionaryOptions];
    },
    statusOptions() {
      return [{ value: '', text: '全部狀態' }, { value: 'open', text: '報名中' }];
    },
    sortModeOptions() {
      return [
        { value: '', text: '預設排序' },
        { value: 'productStartAsc', text: '課程日期：近到遠' },
        { value: 'productStartDesc', text: '課程日期：遠到近' },
        { value: 'applyStartAsc', text: '報名日期：近到遠' },
        { value: 'applyStartDesc', text: '報名日期：遠到近' },
      ];
    },
    filteredList() {
      let items = this.list;
      if (this.selectedStatus === 'open') {
        items = items.filter((item) => this.applyStatus(item).text === '招生中');
      }
      const sortConfig = SORT_FIELD_MAP[this.sortMode];
      if (sortConfig) {
        const [field, direction] = sortConfig;
        items = [...items].sort((a, b) => {
          const av = a[field] ? new Date(a[field]).getTime() : null;
          const bv = b[field] ? new Date(b[field]).getTime() : null;
          if (av === null && bv === null) return 0;
          if (av === null) return 1;
          if (bv === null) return -1;
          return (av - bv) * direction;
        });
      }
      return items;
    },
    programGroups() {
      return groupCampOverview(this.filteredList);
    },
    hasActiveFilters() {
      return Boolean(
        this.selectedSeriesId ||
          this.selectedSeasonType ||
          this.selectedStatus ||
          this.sortMode ||
          this.keywords.trim(),
      );
    },
  },
  mounted() {
    const seasonQuery = this.$route.query.season;
    if (seasonQuery) this.selectedSeasonType = seasonQuery;
    this.getSeriesList();
    this.getSeasonOptions();
    this.getList();
  },
  methods: {
    getSeasonOptions() {
      apiGetCampSeasonTypeSelector()
        .then((res) => { this.seasonTypeDictionaryOptions = Array.isArray(res.data) ? res.data : []; })
        .catch(() => { this.seasonTypeDictionaryOptions = []; });
    },
    getSeriesList() {
      apiGetCampSeries({ Search: {}, Sort: {}, Paging: { pageNumber: 1, pageSize: 9999 } })
        .then((res) => { this.seriesList = Array.isArray(res.data) ? res.data : []; })
        .catch(() => { this.seriesList = []; });
    },
    getList() {
      this.loading = true;
      const search = {};
      const keyword = this.keywords.trim();
      if (keyword) search.keywords = encodeURI(keyword);
      if (this.selectedSeriesId) search.campSeriesId = this.selectedSeriesId;
      if (this.selectedSeasonType) search.seasontype = this.selectedSeasonType;
      apiGetCampOverviewList({ Search: search, Sort: {}, Paging: { pageNumber: 1, pageSize: 9999 } })
        .then((res) => {
          this.list = Array.isArray(res.data) ? res.data : [];
          const expanded = {};
          this.list.forEach((item) => { if (item.ProgramId) expanded[item.ProgramId] = true; });
          this.expandedPrograms = expanded;
        })
        .catch(() => { this.list = []; })
        .finally(() => { this.loading = false; });
    },
    clearFilters() {
      this.selectedSeriesId = '';
      this.selectedSeasonType = '';
      this.selectedStatus = '';
      this.sortMode = '';
      this.keywords = '';
      this.getList();
    },
    toggleProgram(id) { this.$set(this.expandedPrograms, id, !this.isExpanded(id)); },
    isExpanded(id) { return this.expandedPrograms[id] !== false; },
    goProgram(programId) { this._go(`./campProgram?id=${programId}`); },
    productName(item) { return item.ProductName || item.ProductTitle || '未命名梯次'; },
    gradeText(item) {
      if (!item.GradeMin && !item.GradeMax) return '不限';
      if (item.GradeMin && item.GradeMax) return `${item.GradeMin}－${item.GradeMax} 年級`;
      return item.GradeMin ? `${item.GradeMin} 年級以上` : `${item.GradeMax} 年級以下`;
    },
    dateRange(start, end) {
      const format = (value) => { if (!value) return ''; const date = new Date(value); return Number.isNaN(date.getTime()) ? '' : `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`; };
      const startText = format(start); const endText = format(end);
      return startText && endText ? `${startText}－${endText}` : startText || endText || '—';
    },
    applyStatus(item) {
      if (!item || item.ProductEnable !== true) return { text: '未開放', variant: 'secondary' };
      const now = new Date(); const start = item.ApplyStart ? new Date(item.ApplyStart) : null; const end = item.ApplyEnd ? new Date(item.ApplyEnd) : null;
      if (start && !Number.isNaN(start.getTime()) && now < start) return { text: '尚未開放', variant: 'secondary' };
      if (end && !Number.isNaN(end.getTime()) && now > end) return { text: '已截止', variant: 'secondary' };
      if (item.ProductIsSoldOut === true) return { text: '額滿', variant: 'danger' };
      return { text: '招生中', variant: 'success' };
    },
    money(value) { return Number(value || 0).toLocaleString('zh-TW'); },
    earlyBirdActive(item) {
      if (item.EarlyBird !== true || item.EarlyBirdPrice == null) return false;
      const end = item.EarlyBirdEnd ? new Date(item.EarlyBirdEnd) : null;
      return !end || (!Number.isNaN(end.getTime()) && end >= new Date());
    },
  },
};
</script>

<style scoped>
.camp-overview-page { min-height: 100vh; padding: 6rem 0 3rem; background: #f8fbfd; color: #004e7a; }
.page-header, .program-header, .program-heading, .program-actions, .mobile-product-main { display: flex; align-items: center; justify-content: space-between; gap: 1rem; }
.page-header { margin-bottom: 1.25rem; }
.page-header h1 { margin: 0; font-size: 2rem; font-weight: 700; color: #17324d; }
.overview-toolbar { display: flex; flex-wrap: wrap; align-items: center; gap: .6rem; }
.toolbar-select { flex: 1 1 150px; min-width: 120px; max-width: 200px; min-height: 44px; }
.overview-search-group { display: flex; flex: 2 1 520px; align-items: stretch; gap: .6rem; min-width: 360px; }
.overview-toolbar .overview-search { flex: 1 1 auto; min-width: 0; margin-left: 0; }
.clear-filters-button { flex: 0 0 auto; min-height: 44px; white-space: nowrap; }
.overview-search input, .overview-search button { min-height: 44px; }
.program-card { overflow: hidden; margin-bottom: 1.5rem; border: 1px solid #acd8f3; border-radius: 12px; background: #fff; box-shadow: 0 4px 14px rgba(0, 78, 122, .05); }
.program-header { width: 100%; min-height: 72px; padding: 1rem 1.25rem; border: 0; background: #f0f9ff; color: #17324d; text-align: left; }
.program-heading { justify-content: flex-start; }
.program-heading strong, .program-heading small { display: block; }
.program-heading strong { font-size: 1.15rem; }
.program-icon { display: grid; width: 40px; height: 40px; flex: 0 0 40px; place-items: center; border-radius: 50%; background: #d8effd; color: #007ac2; }
.product-count { padding: .35rem .8rem; border: 1px solid #acd8f3; border-radius: 999px; color: #007ac2; white-space: nowrap; }
.common-benefits { display: flex; align-items: center; gap: .6rem; padding: .75rem 1.25rem; border-top: 1px solid #d8effd; background: #f0f9ff; color: #0067a3; }
.common-benefits-label { display: flex; flex: 0 0 auto; align-items: center; font-weight: 600; white-space: nowrap; }
.benefit-scroll { display: flex; flex: 1 1 auto; flex-wrap: nowrap; gap: .5rem; min-width: 0; overflow-x: auto; -webkit-overflow-scrolling: touch; scrollbar-width: thin; }
.benefit-chip {
  position: relative;
  display: inline-flex;
  flex-direction: column;
  gap: .1rem;
  padding: .35rem .9rem .35rem 1.6rem;
  clip-path: polygon(14px 0, 100% 0, 100% 100%, 14px 100%, 0 50%);
  flex: 0 0 auto;
  white-space: nowrap;
  background: #0d84c9;
  color: #fff;
}
.benefit-chip::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 6px;
  width: 5px;
  height: 5px;
  margin-top: -2.5px;
  border-radius: 50%;
  background: #fff;
  opacity: .85;
}
.benefit-chip-title { color: #fff; font-weight: 600; font-size: .85rem; }
.benefit-list { display: flex; flex-wrap: nowrap; gap: .35rem; max-width: 100%; overflow-x: auto; -webkit-overflow-scrolling: touch; scrollbar-width: thin; }
.desktop-products table { width: 100%; border-collapse: collapse; color: #253746; }
.desktop-products th, .desktop-products td { padding: 1rem .8rem; border-top: 1px solid #dfe8ee; vertical-align: middle; }
.desktop-products th { color: #405464; font-size: .9rem; }
.desktop-products th:nth-child(5), .desktop-products td:nth-child(5) { max-width: 240px; }
.desktop-products tbody tr { transition: all .3s; }
.desktop-products tbody tr:hover { background: #ffffe3; }
.desktop-products .btn, .mobile-product .btn { min-height: 44px; white-space: nowrap; }
.price-display { display: grid; gap: .15rem; color: #253746; }
.price-display strong { color: #e46d00; }
.price-display del { color: #6c757d; font-size: .8rem; }
.price-display small { color: #e46d00; }
.mobile-products { display: grid; gap: .85rem; padding: .85rem; }
.mobile-product { padding: 1rem; border: 1px solid #dfe8ee; border-radius: 12px; background: #fff; color: #253746; box-shadow: 0 2px 10px rgba(0, 78, 122, .06); }
.mobile-product-main { align-items: flex-start; margin-bottom: .6rem; padding-bottom: .6rem; border-bottom: 1px solid #eef2f6; }
.mobile-product-main small, .mobile-product-main strong { display: block; }
.mobile-product-main small { color: #6c757d; font-weight: 600; }
.mobile-product-main strong { margin-top: .2rem; font-size: 1.1rem; color: #17324d; }
.mobile-product dl { margin-bottom: .9rem; }
.mobile-product dl > div { display: grid; grid-template-columns: 5.75rem 1fr; align-items: start; gap: .5rem; padding: .45rem 0; min-width: 0; }
.mobile-product dl > div + div { border-top: 1px dashed #eef2f6; }
.mobile-product dt, .mobile-product dd { margin: 0; min-width: 0; font-size: .95rem; }
.mobile-product dt { display: flex; align-items: center; gap: .55rem; padding-top: .1rem; color: #8a97a3; font-weight: 500; }
.mobile-product dt .dt-icon { flex: 0 0 auto; color: #7fa6bd; }
.mobile-product dt .dt-text { flex: 1 1 auto; text-align: justify; text-align-last: justify; text-justify: inter-character; }
.mobile-product dd { text-align: right; }
.mobile-product dd .benefit-list { justify-content: flex-end; }
@media (max-width: 767.98px) {
  .camp-overview-page { padding-top: 5rem; }
  .page-header h1 { font-size: 1.65rem; }
  .overview-search-group { width: 100%; min-width: 0; flex-direction: column; gap: .6rem; }
  .overview-search { width: 100%; max-width: none; margin-left: 0; }
  .clear-filters-button { width: 100%; }
  .toolbar-select { flex: 1 1 auto; max-width: none; }
  .program-header { padding: .8rem; }
  .program-heading { gap: .6rem; }
  .program-heading strong { font-size: 1rem; }
  .program-actions { gap: .5rem; }
  .product-count { padding: .25rem .55rem; font-size: .8rem; }
  .common-benefits { padding: .7rem .8rem; }
}

@media (min-width: 768px) and (max-width: 1199.98px) {
  .overview-toolbar .toolbar-select { flex: 1 1 calc(25% - .6rem); min-width: 0; max-width: none; }
  .overview-search-group { flex: 1 1 100%; min-width: 0; }
  .overview-toolbar .overview-search { max-width: none; }
}
</style>
